import {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { FlagIcon } from "lucide-react";

import type { OpenAPI } from "simplydo/interfaces";
import { styled } from "simplydo/react/styled";
import { Icon } from "simplydo/react/ui/icon";
import { Loader } from "simplydo/react/ui/loader";
import { Menu, MenuItem } from "simplydo/react/ui/menu";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { useIdea } from "components/ideas/hooks/useIdea";
import type { ItemRefs } from "components/ideas/useIdeaScrollTracking";
import { HoverActionsContentBox } from "components/lib/HoverActions";
import ReportContentChip from "components/lib/ReportContent/ReportContentChip";
import { TooltipButton } from "components/lib/UI";

import DownloadCoverImageAction from "../Actions/DownloadCoverImageAction";
import DownloadPDFAction from "../Actions/DownloadPDFAction";
import FollowAction from "../Actions/FollowAction";
import JoinTeamAction from "../Actions/JoinTeamAction";
import LabelAction from "../Actions/LabelAction";
import LikeAction from "../Actions/LikeAction";
import PinAction from "../Actions/PinAction";
import ScrollToCommentsAction from "../Actions/ScrollToCommentsAction";
import TagAction from "../Actions/TagAction";
import type { IdeaPreviewChallenge, IdeaPreviewIdea } from "../types";
import ActivityManagement from "./ActivityManagement";
import { AssessorManagement } from "./AssessorManagement";
import EmailManagement from "./EmailManagement/EmailManagement";
import { IdeaAssessment } from "./IdeaAssessment";
import ImpactManagement from "./ImpactManagement/ImpactManagement";
import ImpactStatementManagement from "./ImpactStatementManagement";
import { ProjectManagement } from "./ProjectManagement";

const ActionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  width: 100%;
  height: 100%;

  .content-box {
    border-radius: 5px;
  }
`;

const SubMenuItemContainer = styled.div`
  overflow: auto;
  padding-left: 5px;
  padding-right: 10px;
`;

const ProjectTabs = {
  OVERVIEW: "overview",
  EMAILS: "emails",
  ASSIGNEES: "assignees",
  ASSESSMENT: "assessment",
  ASSESSORS: "assessors",
  ACTIVITY: "activity",
  IMPACT: "impact",
};

type PreviewSideWindowProps = {
  updateProjectLane: (
    ideaIds: string[],
    laneId: string,
    position: number,
  ) => void;
  assessmentEnabled?: boolean;
  challenge?: IdeaPreviewChallenge;
  sectionRefs?: RefObject<ItemRefs>;
  fieldRefs?: RefObject<ItemRefs>;
  scrollRef?: RefObject<HTMLDivElement | null>;
};

const PreviewSideWindow = ({
  updateProjectLane,
  assessmentEnabled,
  challenge: propChallenge,
  sectionRefs,
  fieldRefs,
  scrollRef,
}: PreviewSideWindowProps) => {
  const [idea, ideaQuery] = useIdea();
  const [searchParams] = useSearchParams();
  const paramComment = searchParams.get("comment");
  const user = useAppSelector((state) => state.user);

  const ideaId = idea?._id;
  const challengeId = idea?.challenge;

  const board = useMemo(
    () =>
      idea?.projectManagement?.boards?.find((b) => b.forId === idea.challenge),
    [idea],
  );
  const userIsAssigned = useMemo(
    () =>
      !!(board?.assignees || []).find((assigneeId) => assigneeId === user?._id),
    [user, board],
  );
  const userIsAssessor = useMemo(
    () =>
      !!(idea?.assessments || []).find(
        (assessment) => assessment?.user === user?._id,
      ),
    [idea, user],
  );

  const canManage = util.canManageChallenge(user!, idea?.ownerChallenge);
  const canViewImpacts =
    canManage ||
    userIsAssigned ||
    util.hasPermission(user!, "challenge.viewProjectImpacts", challengeId);

  const canViewProjectBoard =
    userIsAssigned ||
    canManage ||
    util.hasPermission(user!, "challenge.viewProjectBoard", challengeId);

  const canViewProjectActivity = util.hasPermission(
    user!,
    "challenge.viewProjectActivity",
    challengeId,
  );
  const canViewAssessments = util.hasPermission(
    user!,
    "challenge.viewAssessments",
    challengeId,
  );
  const canViewProjectEmails = util.hasPermission(
    user!,
    "challenge.viewProjectEmails",
    challengeId,
  );
  const canSendProjectEmails = util.hasPermission(
    user!,
    "challenge.sendProjectEmails",
    challengeId,
  );
  const canViewProjectAssignees = util.hasPermission(
    user!,
    "challenge.viewProjectAssignees",
    challengeId,
  );
  const canEditProjectAssignees = util.hasPermission(
    user!,
    "challenge.editProjectAssignees",
    challengeId,
  );
  const canViewProjectNotes = util.hasPermission(
    user!,
    "challenge.viewProjectNotes",
    challengeId,
  );
  const canCreateProjectNotes = util.hasPermission(
    user!,
    "challenge.createProjectNotes",
    challengeId,
  );
  const canEditProjectDates = util.hasPermission(
    user!,
    "challenge.editProjectDates",
    challengeId,
  );

  const canViewTabMenu =
    canViewProjectBoard ||
    canManage ||
    userIsAssessor ||
    userIsAssigned ||
    canViewImpacts ||
    canViewProjectActivity ||
    canViewAssessments ||
    canViewProjectAssignees ||
    canEditProjectAssignees ||
    canViewProjectNotes ||
    canCreateProjectNotes ||
    canEditProjectDates ||
    canViewProjectEmails ||
    canSendProjectEmails;

  const [activeProjectTab, setActiveProjectTab] = useState(
    userIsAssessor && !paramComment
      ? ProjectTabs.ASSESSMENT
      : ProjectTabs.OVERVIEW,
  );

  useEffect(() => {
    setActiveProjectTab(
      userIsAssessor && !paramComment
        ? ProjectTabs.ASSESSMENT
        : ProjectTabs.OVERVIEW,
    );
  }, [ideaId, userIsAssessor, paramComment]);

  const [impactList, setImpactList] = useState<
    OpenAPI.Schemas["OrganisationImpact"][]
  >([]);
  const { t } = useTranslation();

  const usingChallenge = useMemo(
    () => propChallenge ?? idea?.ownerChallenge,
    [propChallenge, idea],
  );

  useEffect(() => {
    if (user?.ownerOrganisation?._id) {
      api.organisations.getImpacts(
        user.ownerOrganisation._id,
        ({ impacts }) => {
          setImpactList(impacts);
        },
        () => {},
      );
    }
  }, [user?.ownerOrganisation?._id]);

  const handleUpdateProjectLane = useCallback(
    (laneId: string) => {
      if (ideaId) {
        updateProjectLane([ideaId], laneId, 0);
      }
    },
    [ideaId, updateProjectLane],
  );

  const impactEnabled =
    user?.ownerOrganisation?.enabledFeatures?.includes("ideaImpacts");
  const impactStatementsEnabled =
    user?.ownerOrganisation?.enabledFeatures?.includes("impactStatements") &&
    usingChallenge?.impactStatements?.enabled;

  return (
    <ActionContainer data-testid="simplydo-project-board-actions">
      <HoverActionsContentBox
        $large
        $isOpen={canViewTabMenu || ideaQuery.isLoading}
      >
        {idea ? (
          <div className="content-box">
            <div
              style={{
                display: "flex",
                position: "relative",
                flexDirection: canViewTabMenu ? "row" : "column",
                gap: canViewTabMenu ? 0 : 4,
              }}
            >
              <LikeAction idea={idea as IdeaPreviewIdea} />
              <ScrollToCommentsAction idea={idea as IdeaPreviewIdea} />
              <FollowAction idea={idea as IdeaPreviewIdea} />
              <JoinTeamAction idea={idea as IdeaPreviewIdea} />

              <DownloadPDFAction idea={idea as IdeaPreviewIdea} />
              <DownloadCoverImageAction idea={idea as IdeaPreviewIdea} />

              <LabelAction idea={idea as IdeaPreviewIdea} />
              <TagAction idea={idea as IdeaPreviewIdea} />
              <PinAction idea={idea as IdeaPreviewIdea} />

              <div
                style={{
                  width: canViewTabMenu ? 1 : 24,
                  height: canViewTabMenu ? 24 : 1,
                  alignSelf: "center",
                  marginLeft: 2,
                  marginRight: 5,
                  background: "rgba(0, 0, 0, 0.1)",
                }}
              />

              <ReportContentChip
                forId={ideaId}
                forType="idea"
                trigger={
                  <TooltipButton tooltip="Report">
                    <Icon icon={FlagIcon} />
                  </TooltipButton>
                }
              />
            </div>
            {canViewTabMenu ? (
              <>
                <Menu
                  size="small"
                  style={{
                    marginTop: 12,
                    overflowX: "auto",
                    overflowY: "hidden",
                    scrollbarWidth: "thin",
                    scrollbarColor: "#d0d0d0 #fff",
                  }}
                >
                  {canViewProjectBoard ||
                  canViewProjectAssignees ||
                  canEditProjectAssignees ||
                  canViewProjectNotes ||
                  canCreateProjectNotes ||
                  canEditProjectDates ? (
                    <MenuItem
                      active={activeProjectTab === ProjectTabs.OVERVIEW}
                      onClick={() => setActiveProjectTab(ProjectTabs.OVERVIEW)}
                    >
                      Overview
                    </MenuItem>
                  ) : null}
                  {userIsAssessor ? (
                    <MenuItem
                      active={activeProjectTab === ProjectTabs.ASSESSMENT}
                      onClick={() =>
                        setActiveProjectTab(ProjectTabs.ASSESSMENT)
                      }
                    >
                      Your assessments
                    </MenuItem>
                  ) : null}
                  {(userIsAssigned || canManage || canViewAssessments) &&
                  assessmentEnabled ? (
                    <MenuItem
                      active={activeProjectTab === ProjectTabs.ASSESSORS}
                      onClick={() => setActiveProjectTab(ProjectTabs.ASSESSORS)}
                    >
                      Assessments
                    </MenuItem>
                  ) : null}
                  {canViewProjectEmails || canSendProjectEmails || canManage ? (
                    <MenuItem
                      active={activeProjectTab === ProjectTabs.EMAILS}
                      onClick={() => setActiveProjectTab(ProjectTabs.EMAILS)}
                    >
                      Emails
                    </MenuItem>
                  ) : null}
                  {userIsAssigned || canManage || canViewProjectActivity ? (
                    <MenuItem
                      active={activeProjectTab === ProjectTabs.ACTIVITY}
                      onClick={() => setActiveProjectTab(ProjectTabs.ACTIVITY)}
                    >
                      Activity
                    </MenuItem>
                  ) : null}

                  {impactEnabled &&
                    canViewImpacts &&
                    impactList?.length > 0 && (
                      <MenuItem
                        active={activeProjectTab === ProjectTabs.IMPACT}
                        onClick={() => setActiveProjectTab(ProjectTabs.IMPACT)}
                      >
                        {t("common:capitalise", { key: "generic.impact" })}
                      </MenuItem>
                    )}
                </Menu>
                {(canViewProjectBoard ||
                  canViewProjectAssignees ||
                  canEditProjectAssignees ||
                  canViewProjectNotes ||
                  canCreateProjectNotes ||
                  canEditProjectDates) &&
                activeProjectTab === ProjectTabs.OVERVIEW ? (
                  <SubMenuItemContainer>
                    <ProjectManagement
                      idea={idea}
                      handleUpdateProjectLane={handleUpdateProjectLane}
                      canManage={canManage}
                    />
                  </SubMenuItemContainer>
                ) : null}
                {userIsAssessor &&
                activeProjectTab === ProjectTabs.ASSESSMENT ? (
                  <SubMenuItemContainer>
                    <IdeaAssessment
                      idea={idea}
                      sectionRefs={sectionRefs}
                      fieldRefs={fieldRefs}
                      scrollRef={scrollRef}
                    />
                  </SubMenuItemContainer>
                ) : null}
                {(canViewProjectEmails || canSendProjectEmails || canManage) &&
                activeProjectTab === ProjectTabs.EMAILS ? (
                  <SubMenuItemContainer>
                    <EmailManagement idea={idea} />
                  </SubMenuItemContainer>
                ) : null}
                {(userIsAssigned || canManage || canViewAssessments) &&
                  activeProjectTab === ProjectTabs.ASSESSORS && (
                    <SubMenuItemContainer>
                      <AssessorManagement
                        idea={idea}
                        challenge={usingChallenge}
                      />
                    </SubMenuItemContainer>
                  )}
                {(userIsAssigned || canManage || canViewProjectActivity) &&
                activeProjectTab === ProjectTabs.ACTIVITY ? (
                  <SubMenuItemContainer>
                    <ActivityManagement key={idea._id} idea={idea} />
                  </SubMenuItemContainer>
                ) : null}
                {activeProjectTab === ProjectTabs.IMPACT &&
                impactEnabled &&
                !impactStatementsEnabled &&
                canViewImpacts &&
                impactList?.length > 0 ? (
                  <SubMenuItemContainer>
                    <ImpactManagement
                      idea={idea}
                      impactList={impactList}
                      canManage={userIsAssigned || canManage}
                    />
                  </SubMenuItemContainer>
                ) : null}
                {activeProjectTab === ProjectTabs.IMPACT &&
                impactEnabled &&
                impactStatementsEnabled &&
                canViewImpacts ? (
                  <SubMenuItemContainer>
                    <ImpactStatementManagement ideaId={idea._id} />
                  </SubMenuItemContainer>
                ) : null}
              </>
            ) : null}
          </div>
        ) : (
          <div
            className="content-box"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Loader />
          </div>
        )}
      </HoverActionsContentBox>
    </ActionContainer>
  );
};

export default PreviewSideWindow;
